/* eslint-disable react/jsx-filename-extension */
/*eslint no-restricted-imports: ["error", "fs"]*/
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { func } from "prop-types";
import ReduxToastr from "react-redux-toastr";
import GtmProvider from "./providers/analytics";
import { Provider, connect, useSelector } from "react-redux";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { getAccessToken } from "./utils/tokens";
import Locations from "./pages/Locations";
import Communications from "./pages/Communications";
import NoShow from "./pages/Reports/NoShow";
import Appointments from "./pages/Appointments";
import MigrationAssistant from "./pages/MigrationAssistant/App";
import Grow from "./pages/Apps/Grow";
import AppConfiguration from "./pages/Apps/Train";
import Apps from "./pages/Apps";
import AppPage from "./pages/Apps/App";
import { CalendarSync } from "./pages/CalendarSync";
import Calendar from "./pages/Calendar";
import Plans from "./pages/Plans";
import configureStore from "./redux/store/spa";
import { subdomain } from "./utils/requests";
import routes from "./routes/routes-list";
import { AppContext } from "./context/app";
import * as clientActions from "./redux/actions/clientActions";
import * as userActions from "./redux/actions/userActions";
import { ToastContainer } from "react-toastify";
import ApolloContext from "./context/apollo";
import ChurnReport from "./pages/Reports/ChurnReport";
import Availability from "./pages/Availability";
import ReactQueryProvider from "./components/providers/ReactQueryProvider";
import AnalyticsProvider, {
  useAnalytics,
} from "./components/providers/AnalyticsProvider";

import ConfirmProvider from "./components/providers/ConfirmProvider";
import Report from "./pages/Reports";
import { env } from "./env";
import { checkIfHasAccessToRoute } from "./routes/util";
import FeatureFlagContextProvider from "./context/features";
import { RoutesUtilsProvider } from "./context/routeUtils";
import AttendanceReport from "./pages/Calendar/AttendanceReport/AttendanceReport";
import PageLoader from "./components/PageLoader";
import GetStartedRouter from "./pages/GetStarted";
import CommittedClub from "./pages/CommittedClub";
import DashboardRouter from "./pages/Dashboard";
import Celebrations from "./pages/Reports/Celebrations/Celebrations";
import ExperimentProvider from "./components/providers/ExperimentProvider";
import PaymentMigration from "./pages/MigrationAssistant/PaymentMigration/PaymentMigration";
import Payment from "./pages/Payment/App";
import People from "./pages/People/App";
import Users from "./pages/Users/App";
import SignUpRouter from "./pages/SignUp";
import EmbeddedApps from "./pages/EmbeddedApps/";
import Train from "./pages/Train";
import "./index.tailwind.css";
import "@pushpress/pushpress-ui/tailwind.css";
import { initDatadog } from "./utils/datadog";
import axiosRetry, { exponentialDelay } from "axios-retry";
import axios from "axios";
import { getSubdomains } from "./utils/browserUtils";
import HubspotProvider from "./components/providers/HubspotProvider/HubspotProvider";
import $ from "jquery";
import { growthBookClient } from "./implementations/growthbook/growthbook-client";
import ImmersiveWizardRouter from "./pages/ImmersiveWizard";

window.$ = $;
window.jQuery = $;

axiosRetry(axios, { retries: 3, retryDelay: exponentialDelay });

// eslint-disable-next-line import/no-unresolved
import "!style-loader!css-loader!react-toastify/dist/ReactToastify.css";

// eslint-disable-next-line import/no-unresolved
import "!style-loader!css-loader!@pushpress/shared-components/build/main.min.css";
import AccountRouter from "./pages/Account";

initDatadog();

const store = configureStore({
  sidebar: {
    routes,
    isOpen: true,
    isSticky: false,
  },
});

const mapDispatchToProps = {
  ...clientActions,
  ...userActions,
};

const App = ({ fetchClient, fetchUser, getFeatures }) => {
  const [accessToken] = useState(getAccessToken());

  const client = useSelector((state) => state.client.data);
  const features = useSelector((state) => state.client.features);
  const user = useSelector((state) => state.user.data);
  const currentSubdomain = getSubdomains()[0] || "";
  const isSignupFlow = currentSubdomain === "signup";

  useEffect(() => {
    if (isSignupFlow) {
      return;
    }

    if (!accessToken) {
      if (window.location.pathname !== "/dashboard/login") {
        window.location.href = "/dashboard/login";
      }
    } else {
      fetchClient(accessToken, subdomain).then(() => {
        return Promise.all([
          fetchUser(accessToken, user.user_uuid),
          getFeatures(),
        ]);
      });
    }
  }, [
    fetchUser,
    fetchClient,
    accessToken,
    getFeatures,
    user.user_uuid,
    isSignupFlow,
  ]);

  useEffect(() => {
    if (user.role && !checkIfHasAccessToRoute(user, window.location.pathname)) {
      window.location.href = "/";
    }
  }, [user, user.role]);

  const hasClient = () => {
    return client && client.uuid !== null && typeof client.uuid !== "undefined";
  };

  const hasFeatures = () => {
    return features && Object.keys(features).length >= 1;
  };

  const hasUser = () => {
    return user && user.uuid !== null && typeof user.user_uuid !== "undefined";
  };

  const { trackUser } = useAnalytics();

  useEffect(() => {
    if (user && client) {
      trackUser(user.user_uuid, { ...user, client });
    }
  }, [user, client, trackUser]);

  if (isSignupFlow) {
    return (
      <AppContext.Provider
        value={{
          user,
          client,
          spa: true,
          stripeConfig: {
            publishableKey: env.STRIPE_PAYMENTS_PUBLISHABLE_KEY,
          },
        }}
      >
        <ApolloContext>
          <HubspotProvider>
            <FeatureFlagContextProvider>
              <ExperimentProvider client={growthBookClient}>
                <ToastContainer />
                <SignUpRouter />
              </ExperimentProvider>
            </FeatureFlagContextProvider>
          </HubspotProvider>
        </ApolloContext>
      </AppContext.Provider>
    );
  }

  const hasRequired =
    !!accessToken && hasClient() && hasUser() && hasFeatures();

  if (
    !hasRequired ||
    !checkIfHasAccessToRoute(user, window.location.pathname)
  ) {
    return <PageLoader />;
  }

  return (
    <>
      <AppContext.Provider
        value={{
          user,
          client,
          spa: true,
          stripeConfig: {
            publishableKey: env.STRIPE_PAYMENTS_PUBLISHABLE_KEY,
          },
        }}
      >
        <ApolloContext>
          <FeatureFlagContextProvider>
            <ExperimentProvider client={growthBookClient}>
              <ToastContainer />
              <ConfirmProvider>
                <RoutesUtilsProvider>
                  <GtmProvider>
                    <Switch>
                      <Route exact path="/main" component={DashboardRouter} />
                      <Route exact path="/signup" component={SignUpRouter} />
                      <Route
                        exact
                        path="/onboarding"
                        component={ImmersiveWizardRouter}
                      />
                      <Route
                        exact
                        path="/migration-assistant/payment-migration"
                        component={PaymentMigration}
                      />
                      <Route path="/user/:userUuid" component={Users} />
                      <Route path="/people" component={People} />
                      <Route path="/account" component={AccountRouter} />
                      <Route path="/start" component={GetStartedRouter} />
                      <Route path="/plans" component={Plans} />
                      <Route path="/schedule" component={Calendar} />
                      <Route path="/locations" component={Locations} />
                      <Route
                        path="/communications"
                        component={Communications}
                      />
                      <Route path="/appointments" component={Appointments} />
                      <Route
                        path="/migration-assistant"
                        component={MigrationAssistant}
                      />
                      <Route
                        path="/no-show"
                        component={() => <NoShow client={client} user={user} />}
                      />
                      <Route path="/committed-club" component={CommittedClub} />
                      {/* App Store */}
                      <Route path="/app-store" exact component={Apps} />
                      {/* App Store Item */}
                      <Route path="/app-store/:slug" component={AppPage} />
                      {/* New Grow App Route */}
                      <Route
                        path="/v2/integrations/app/pushpress-grow"
                        component={() => <Grow client={client} user={user} />}
                      />
                      {/* Old Grow App Route */}
                      <Route
                        path="/integrations/app/v2-pushpress-grow"
                        component={() => <Grow client={client} user={user} />}
                      />
                      {/* Train App Store Page*/}
                      <Route
                        path="/v2/integrations/app/:id"
                        component={() => (
                          <AppConfiguration client={client} user={user} />
                        )}
                      />
                      <Route
                        path="/users/sync/:userUuid"
                        component={CalendarSync}
                      />
                      {/* TODO: Should be removed when nothing uses it anymore */}
                      <Route path="/reports/churn" component={ChurnReport} />
                      <Route
                        path="/reports/celebrations"
                        component={Celebrations}
                      />
                      <Route path="/reporting/:slug" component={Report} />
                      <Route
                        path="/attendance/member/:userUuid"
                        component={AttendanceReport}
                      />
                      <Route
                        path="/availability/:userUuid"
                        component={Availability}
                      />
                      <Route path="/train" component={Train} />
                      <Route path="/app" component={EmbeddedApps} />
                    </Switch>
                  </GtmProvider>
                </RoutesUtilsProvider>
              </ConfirmProvider>
            </ExperimentProvider>
          </FeatureFlagContextProvider>
        </ApolloContext>
      </AppContext.Provider>
    </>
  );
};

App.propTypes = {
  fetchClient: func.isRequired,
  fetchUser: func.isRequired,
  getFeatures: func.isRequired,
};

const ConnectedApp = connect(null, mapDispatchToProps)(App);
const isAddPaymentRoute = window.location.pathname === "/add-payment";

const root = createRoot(document.getElementById("root"));

root.render(
  <ReactQueryProvider>
    <Provider store={store}>
      <Router>
        <AnalyticsProvider
          segmentWriteKey={env.SEGMENT_WRITE_KEY}
          heapAnalyticsAppId={env.HEAP_ANALYTICS_APP_ID}
        >
          {isAddPaymentRoute ? (
            <>
              <ToastContainer />
              <Payment />
            </>
          ) : (
            <>
              <ReduxToastr
                timeOut={5000}
                position="bottom-center"
                transitionIn="fadeIn"
                transitionOut="fadeOut"
                progressBar={false}
                closeOnToastrClick
              />
              <ConnectedApp />
            </>
          )}
        </AnalyticsProvider>
      </Router>
    </Provider>
  </ReactQueryProvider>,
);
